// Was an Speichern am Geraet haengt - und ob dort ein Clonefile liegt.
//
// ANLASS: ein Clonefile laesst sich per API nicht ERZEUGEN. Samsung
// dokumentiert bei webapis.clone nur setCloneUpdateStatusListener,
// restoreCloneData und unSetCloneUpdateStatusListener (Partner-Training "TEP
// Web API's - Clone", 06/2026), und die Aufzaehlung am Geraet bestaetigt das:
// getVersion, restoreCloneData und die beiden Listener, kein store/save/export.
// LG kennt in hcap.system nur requestCloning. Der Weg vom TV zum Clonefile
// bleibt also das Hotelmenue auf einen USB-Stick.
//
// DIE OFFENE FRAGE ist eine andere: liegt eine so erzeugte Datei hinterher an
// einer Stelle, an die diese App herankommt? Dann muesste niemand mehr den
// Stick zum Rechner tragen - die App koennte sie hochladen und der Admin sie
// verteilen. Genau das misst diese Datei, rein LESEND:
//
//   Samsung  tizen.filesystem.listStorages() - welche Speicher es gibt und ob
//            ein USB-Stick gemountet ist; danach die Wurzel und ein
//            Clone-Ordner darin auflisten.
//   LG       hcap.file (nie zuvor abgefragt) und hcap.system.getPeripheralList
//            - welche Datei-Methoden die Firmware ueberhaupt hat.
//
// Geschrieben, geloescht oder verschoben wird nichts.
//
// NICHT MEHR IN get_info. Ein U800F hat sich beim Auslesen neu gestartet,
// sobald zum ersten Mal eine echte Datei auf dem Stick lag - also genau dann,
// wenn der Lesetest lief. get_info ist das meistbenutzte Kommando ueberhaupt;
// ein Geraet, das davon neu startet, ist ein Ausfall im Zimmer. Die Sonde
// haengt deshalb an ihrem eigenen Kommando (`get_files`), und der Lesetest
// laeuft dort auch nur, wenn er ausdruecklich angefordert wird.

import DeviceType from '@/helpers/DeviceType';
import { hasHcapMethod, hcapCall, hcapNamespace } from '@/helpers/LgHcap';

// Zeitbudget je Schritt. Die Filesystem-API arbeitet mit Callbacks; bleibt
// einer aus, soll die Info-Seite trotzdem fertig werden.
const STEP_TIMEOUT_MS = 2000;

// Deckel gegen einen vollen Stick: niemand liest 4000 Dateinamen.
const MAX_ENTRIES = 60;

// Woran ein Clonefile zu erkennen ist. Samsung legt beim Export einen Ordner
// mit "CloneEncrypted.sclone" an (so steht es im Partner-Training), LG schreibt
// eine XML-Datei.
//
// LG-STAND (gemessen auf einem UM767H): hcap.file kennt getUsbStorageList,
// getUsbStorageFileList, downloadFileToUsb und deleteUsbFile. Ein Stick laesst
// sich damit auflisten - HERUNTERLADEN vom Geraet aber nicht: downloadFileToUsb
// geht Netz -> USB, die Gegenrichtung gibt es nicht.
const CLONE_RE = /\.sclone$|\.tlx$|clone/i;

// Die Funktionsnamen eines nativen Objekts - for..in, weil WebIDL-Objekte ihre
// Methoden auf dem Prototyp haben.
function methodNames(api) {
  const out = [];
  if (!api) return out;
  for (const k in api) {
    try {
      if (typeof api[k] === 'function') out.push(k);
    } catch (e) {
      /* manche Properties werfen beim Zugriff */
    }
  }
  return out.sort();
}

function tizenFs() {
  try {
    return typeof tizen !== 'undefined' && tizen && tizen.filesystem ? tizen.filesystem : null;
  } catch (e) {
    return null;
  }
}

function withTimeout(promise, ms, fallback) {
  return Promise.race([
    promise,
    new Promise((resolve) => setTimeout(() => resolve(fallback), ms)),
  ]);
}

function listStorages() {
  const fs = tizenFs();
  if (!fs || typeof fs.listStorages !== 'function') return Promise.resolve(null);
  return withTimeout(
    new Promise((resolve) => {
      try {
        fs.listStorages(
          function(list) {
            resolve(list || []);
          },
          function() {
            resolve(null);
          }
        );
      } catch (e) {
        resolve(null);
      }
    }),
    STEP_TIMEOUT_MS,
    null
  );
}

function resolveDir(label) {
  const fs = tizenFs();
  if (!fs || typeof fs.resolve !== 'function') return Promise.resolve(null);
  return withTimeout(
    new Promise((resolve) => {
      try {
        fs.resolve(
          label,
          function(dir) {
            resolve(dir);
          },
          function() {
            resolve(null);
          },
          'r'
        );
      } catch (e) {
        resolve(null);
      }
    }),
    STEP_TIMEOUT_MS,
    null
  );
}

function listFiles(dir) {
  if (!dir || typeof dir.listFiles !== 'function') return Promise.resolve([]);
  return withTimeout(
    new Promise((resolve) => {
      try {
        dir.listFiles(
          function(files) {
            resolve(files || []);
          },
          function() {
            resolve([]);
          }
        );
      } catch (e) {
        resolve([]);
      }
    }),
    STEP_TIMEOUT_MS,
    []
  );
}

function describe(file) {
  const out = {};
  try {
    out.path = file.fullPath;
  } catch (e) {
    /* manche Implementierungen haben nur name */
  }
  try {
    out.name = file.name;
  } catch (e) {
    /* Bei den virtuellen Wurzeln (images, videos, …) wirft `name`, und der
       Pfad TRAEGT den Namen - gemessen auf einem U800F, wo dadurch ein
       Dutzend Zeilen "?" hiessen. */
    out.name = undefined;
  }
  if (!out.name && out.path) out.name = String(out.path).split('/').pop() || out.path;
  if (!out.name) out.name = '?';
  try {
    out.dir = !!file.isDirectory;
  } catch (e) {
    /* egal */
  }
  try {
    if (!out.dir && file.fileSize !== undefined) out.size = file.fileSize;
  } catch (e) {
    /* egal */
  }
  return out;
}

// Einen Speicher eine Ebene tief ansehen - und einen Clone-Ordner darin noch
// eine Ebene. Tiefer nicht: gesucht wird eine bestimmte Datei, kein Baum.
function firstRegularFile(files) {
  for (let i = 0; i < files.length; i += 1) {
    try {
      // Versteckte Dateien und die Windows-Systemordner der Sticks
      // uebergehen - gesucht ist etwas Normales zum Lesen.
      if (!files[i].isDirectory && String(files[i].name || '').charAt(0) !== '.') return files[i];
    } catch (e) {
      /* weiter */
    }
  }
  return null;
}

function scanStorage(label) {
  return resolveDir(label).then((dir) => {
    if (!dir) return { label: label, readable: false, entries: [] };
    return listFiles(dir).then((files) => {
      const entries = files.slice(0, MAX_ENTRIES).map(describe);
      const sample = firstRegularFile(files);
      const cloneDirs = files.filter((f) => {
        try {
          return f.isDirectory && CLONE_RE.test(String(f.name));
        } catch (e) {
          return false;
        }
      });
      if (cloneDirs.length === 0)
        return { label: label, readable: true, entries: entries, sample: sample };
      return listFiles(cloneDirs[0]).then((inner) => ({
        label: label,
        readable: true,
        entries: entries.concat(inner.slice(0, MAX_ENTRIES).map(describe)),
        sample: sample || firstRegularFile(inner),
      }));
    });
  });
}

/**
 * Base64 aus einem ArrayBuffer - ohne Buffer, den gibt es im WebView nicht.
 *
 * In Haeppchen, weil String.fromCharCode ein Argument je Byte nimmt: ein
 * ganzes Megabyte auf einmal sprengt den Argument-Stapel.
 */
function bufferToBase64(buf) {
  const bytes = new Uint8Array(buf);
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/**
 * Lesetest A: der native Weg - openStream + readBase64.
 *
 * DER VERDAECHTIGE. Ein U800F hat sich beim ersten Lauf ueber einer echten
 * Datei neu gestartet; das Auflisten desselben Sticks lief davor tagelang
 * unauffaellig. Deshalb laeuft er nur auf Anforderung - und als zweiter.
 */
function readTestStream(file) {
  if (!file || typeof file.openStream !== 'function') {
    return Promise.resolve({ via: 'openStream', ok: false, detail: 'openStream fehlt' });
  }
  return withTimeout(
    new Promise((resolve) => {
      try {
        file.openStream(
          'r',
          function(stream) {
            let out;
            try {
              const chunk = stream.readBase64(16);
              out = {
                via: 'openStream',
                ok: true,
                bytes: chunk ? chunk.length : 0,
                sample: String(chunk || '').slice(0, 24),
              };
            } catch (e) {
              out = { via: 'openStream', ok: false, detail: String((e && (e.message || e.name)) || e) };
            }
            try {
              stream.close();
            } catch (e) {
              /* geschlossen ist geschlossen */
            }
            resolve(out);
          },
          function(err) {
            resolve({
              via: 'openStream',
              ok: false,
              detail: String((err && (err.message || err.name)) || err),
            });
          },
          'UTF-8'
        );
      } catch (e) {
        resolve({ via: 'openStream', ok: false, detail: String((e && (e.message || e.name)) || e) });
      }
    }),
    STEP_TIMEOUT_MS,
    { via: 'openStream', ok: false, detail: 'keine Antwort' }
  );
}

/**
 * Lesetest B: der Umweg ueber die Adresse - toURI + XHR.
 *
 * tizen.filesystem.toURI() liefert eine file://-Adresse, und eine
 * privilegierte Tizen-App darf die laden. Damit faellt das native
 * Stream-Handling ganz weg - genau der Verdaechtige aus Lesetest A. Traegt
 * dieser Weg, ist er auch fuer grosse Dateien der bessere.
 */
function readTestUri(file) {
  const api = tizenFs();
  let uri;
  try {
    if (api && typeof api.toURI === 'function') uri = api.toURI(file);
    else if (typeof file.toURI === 'function') uri = file.toURI();
  } catch (e) {
    return Promise.resolve({ via: 'toURI', ok: false, detail: String((e && e.message) || e) });
  }
  if (!uri) return Promise.resolve({ via: 'toURI', ok: false, detail: 'toURI liefert keine Adresse' });

  return withTimeout(
    new Promise((resolve) => {
      try {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', uri, true);
        xhr.responseType = 'arraybuffer';
        xhr.onload = function() {
          try {
            const buf = xhr.response;
            const size = buf ? buf.byteLength : 0;
            resolve({
              via: 'toURI',
              ok: size > 0,
              uri: String(uri).slice(0, 120),
              bytes: size,
              sample: size ? bufferToBase64(buf.slice(0, 16)).slice(0, 24) : '',
            });
          } catch (e) {
            resolve({ via: 'toURI', ok: false, detail: String((e && e.message) || e) });
          }
        };
        xhr.onerror = function() {
          resolve({
            via: 'toURI',
            ok: false,
            uri: String(uri).slice(0, 120),
            detail: 'XHR-Fehler (Status ' + xhr.status + ')',
          });
        };
        xhr.send();
      } catch (e) {
        resolve({ via: 'toURI', ok: false, detail: String((e && (e.message || e.name)) || e) });
      }
    }),
    STEP_TIMEOUT_MS,
    { via: 'toURI', ok: false, detail: 'keine Antwort' }
  );
}

/**
 * Beide Wege nacheinander - der ungefaehrliche zuerst.
 *
 * Die Reihenfolge ist der ganze Trick: wirft der native Weg das Geraet um, ist
 * das Ergebnis des anderen schon gemeldet und damit gerettet.
 */
function runReadTests(file, mode) {
  const wanted = mode === true ? 'both' : String(mode || '');
  const jobs = [];
  if (wanted === 'both' || wanted === 'uri') jobs.push(readTestUri);
  if (wanted === 'both' || wanted === 'stream') jobs.push(readTestStream);
  return jobs.reduce(
    (chain, job) => chain.then((acc) => job(file).then((r) => acc.concat([r]))),
    Promise.resolve([])
  );
}

function probeTizen(readMode) {
  const fs = tizenFs();
  const result = { platform: 'samsung', api: {}, storages: [], files: [] };
  // for..in statt Object.keys: die WebIDL-Objekte legen ihre Methoden auf dem
  // Prototyp ab, Object.keys sieht davon nichts (gemessen: "tizen.filesystem
  // (0)", obwohl listStorages und resolve offensichtlich da sind).
  result.api['tizen.filesystem'] = fs ? methodNames(fs) : [];
  try {
    const c = typeof webapis !== 'undefined' && webapis && webapis.clone ? webapis.clone : null;
    if (c) {
      result.api['webapis.clone'] = methodNames(c);
    }
  } catch (e) {
    /* ohne clone-API eben ohne */
  }
  if (!fs) {
    result.error = 'tizen.filesystem nicht vorhanden';
    return Promise.resolve(result);
  }

  return listStorages().then((storages) => {
    if (!storages) {
      result.error = 'listStorages hat nicht geantwortet';
      return result;
    }
    result.storages = storages.map((s) => {
      const out = {};
      try {
        out.label = s.label;
      } catch (e) {
        out.label = '?';
      }
      try {
        out.type = s.type;
      } catch (e) {
        /* egal */
      }
      try {
        out.state = s.state;
      } catch (e) {
        /* egal */
      }
      return out;
    });

    // Angesehen wird, was gemountet ist - und zuerst das Wechselmedium, denn
    // dorthin schreibt das Hotelmenue das Clonefile.
    const labels = result.storages
      .filter((s) => !s.state || String(s.state).toUpperCase().indexOf('MOUNT') !== -1)
      .map((s) => s.label)
      .filter(Boolean)
      .sort((a, b) => (/removable|usb|sdcard/i.test(a) ? -1 : 0) - (/removable|usb|sdcard/i.test(b) ? -1 : 0))
      .slice(0, 4);

    return labels
      .reduce(
        (chain, label) =>
          chain.then((acc) =>
            scanStorage(label)
              .then((r) => acc.concat([r]))
              .catch(() => acc)
          ),
        Promise.resolve([])
      )
      .then((scans) => {
        result.files = scans;
        // Der Lesetest laeuft an der ersten Datei, die sich anbietet - der
        // Stick zuerst, weil dort das Clonefile landet.
        //
        // NUR AUF ANFORDERUNG. Ein U800F hat sich beim ersten echten Lesetest
        // (openStream + readBase64 auf einer Datei des Sticks) neu gestartet.
        // Der Grund ist nicht geklaert; sicher ist nur, dass ein natives
        // Lesen ein Geraet umwerfen kann - und das darf eine Diagnose nicht.
        const withFile = readMode ? scans.filter((sc) => sc.sample) : [];
        if (!withFile.length) {
          result.files = scans.map((sc) => ({
            label: sc.label,
            readable: sc.readable,
            entries: sc.entries,
          }));
          return result;
        }
        const pick = withFile[0];
        const name = describe(pick.sample).name;
        return runReadTests(pick.sample, readMode).then((results) => {
          result.readTest = { storage: pick.label, file: name, results: results };
          // Die Datei-Objekte selbst gehen nicht auf den Draht - sie sind
          // native Handles, kein JSON.
          result.files = scans.map((sc) => ({
            label: sc.label,
            readable: sc.readable,
            entries: sc.entries,
          }));
          return result;
        });
      });
  });
}

// Die Antwort einer hcap-Methode auf eine Liste hin absuchen. LG benennt die
// Felder je Methode anders (storageList, deviceList, fileList, list) - und was
// nicht passt, faehrt als Rohwert mit, damit der naechste Blick ins Log die
// Form zeigt statt sie raten zu lassen.
function firstArray(s) {
  if (!s || typeof s !== 'object') return null;
  const keys = Object.keys(s);
  for (let i = 0; i < keys.length; i += 1) {
    if (Array.isArray(s[keys[i]])) return s[keys[i]];
  }
  return null;
}

function shortRaw(s) {
  try {
    const copy = {};
    Object.keys(s || {}).forEach((k) => {
      if (k === 'onSuccess' || k === 'onFailure') return;
      copy[k] = s[k];
    });
    return JSON.stringify(copy).slice(0, 400);
  } catch (e) {
    return '';
  }
}

// Wie die Dateiliste adressiert wird, ist nicht dokumentiert. Statt zu raten
// wird gemessen: die Firmware sagt in ihrer Absage, was sie vermisst - genau so
// kam bei getInstallerMenuItem heraus, dass der Parameter `key` heisst. Der
// erste Versuch geht deshalb OHNE Parameter raus.
function readUsbFiles(storages) {
  const attempts = [{}];
  (storages || []).slice(0, 2).forEach((st) => {
    const id = st && (st.deviceId || st.storageId || st.id || st.name || st.mountPath || st.path);
    if (id === undefined || id === null) return;
    attempts.push({ deviceId: id });
    attempts.push({ storageId: id });
    attempts.push({ name: id, path: '/' });
  });

  // Der erste Treffer gewinnt; sonst bleibt die letzte Absage stehen - in ihr
  // steht, was die Firmware vermisst.
  return attempts.reduce(
    (chain, params) =>
      chain.then((sofar) => {
        if (sofar && sofar.list) return sofar;
        return hcapCall('file', 'getUsbStorageFileList', params, STEP_TIMEOUT_MS)
          .then((s) => ({ params: params, list: firstArray(s), raw: shortRaw(s) }))
          .catch((f) => ({
            params: params,
            error: String((f && (f.errorMessage || f.message)) || f),
          }));
      }),
    Promise.resolve(null)
  );
}

function probeLg() {
  const result = { platform: 'lg', api: {}, storages: [], files: [] };
  ['file', 'storage', 'usb'].forEach((name) => {
    const api = hcapNamespace(name);
    if (!api) return;
    const names = [];
    for (const k in api) {
      try {
        if (typeof api[k] === 'function') names.push(k);
      } catch (e) {
        /* manche Properties werfen beim Zugriff */
      }
    }
    result.api['hcap.' + name] = names.sort();
  });

  // GEMESSEN auf einem UM767H: hcap.file kennt getUsbStorageList,
  // getUsbStorageFileList, downloadFileToUsb und deleteUsbFile - damit laesst
  // sich ein eingesteckter Stick auflisten. (getPeripheralList aus hcap.system
  // gibt es dort nicht: "get_peripheral_list() is unknown command".)
  if (!hasHcapMethod('file', 'getUsbStorageList')) return Promise.resolve(result);

  return hcapCall('file', 'getUsbStorageList', {}, STEP_TIMEOUT_MS)
    .then((s) => {
      const list = firstArray(s) || [];
      result.storages = list.slice(0, MAX_ENTRIES).map((p) => (p && typeof p === 'object' ? p : { name: String(p) }));
      if (!result.storages.length) result.storagesRaw = shortRaw(s);
      return readUsbFiles(result.storages);
    })
    .then((res) => {
      if (!res) return result;
      if (res.list) {
        result.files = [
          {
            label: 'USB (' + JSON.stringify(res.params) + ')',
            readable: true,
            entries: res.list.slice(0, MAX_ENTRIES).map((f) =>
              f && typeof f === 'object'
                ? { name: f.name || f.fileName || f.path || '?', size: f.size, path: f.path }
                : { name: String(f) }
            ),
          },
        ];
      } else if (res.error) {
        // Der Grund ist die Auskunft: er nennt den Parameter, den die Firmware
        // erwartet.
        result.fileListError = res.error;
      }
      return result;
    })
    .catch((f) => {
      result.error = String((f && (f.errorMessage || f.message)) || f);
      return result;
    });
}

export default class StorageProbe {
  /**
   * Speicher und Dateien ansehen. Liefert immer ein Ergebnis, nie einen
   * Fehler - eine Diagnose darf die Seite, auf der sie steht, nicht aufhalten.
   */
  static read(options) {
    // `readTest`: false (Standard), 'uri', 'stream' oder true fuer beide. Der
    // Lesetest ist der einzige Teil, der eine Datei anfasst - alles andere
    // listet nur auf.
    const readMode = (options && options.readTest) || false;
    try {
      if (DeviceType.isTizen || DeviceType.isOrsay) return probeTizen(readMode).catch(() => null);
      if (DeviceType.isLG) return probeLg().catch(() => null);
    } catch (e) {
      return Promise.resolve(null);
    }
    return Promise.resolve(null);
  }
}



// WEBPACK FOOTER //
// ./src/helpers/StorageProbe.js